import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import format from 'string-template';
import { Alert } from '@mui/material';

import FirebaseContext from '../../context/FirebaseContext';
import LoggedInUserContext from '../../context/LoggedInUserContext';
import useComponentMounted from '../../hooks/useComponentMounted';
import useToast from '../../CoachTools/hooks/useToast';
import { DateFormat } from '../../utils/date';
import { TimeDurationFormat } from '../../utils/time';
import AutoComplete from '../../CoachTools/components/AutoComplete';
import Button from '../../components/Button';
import ConfirmDialog from '../../CoachTools/components/ConfirmDialog';
import LoadingOverlay from '../../CoachTools/components/LoadingOverlay';

import BookingSuccess from './components/BookingSuccess';
import SlotsList from './components/SlotsList';

import {
  parseQuery,
  REQUIRED_PARAMS,
  CallBookingQueryParams,
  getSlotSeatsAvailability,
  bookLeadCall,
  getLeadDetails,
} from './utils';
import {
  Container,
  Header,
  Title,
  TimeZoneSelectorWrapper,
  CalendarSection,
  MainContent,
  TimeZoneLabel,
  DayCell,
  DayNumber,
  StyledToastContainer,
} from './styles';
import texts from './texts.json';

const timeZones = moment.tz.names();
const timeZoneOptions = timeZones.map((timeZone) => ({
  id: timeZone,
  label: timeZone,
}));

const BookLeadCall = ({ leadId: leadIdProp, name: nameProp, timeZone: timeZoneProp } = {}) => {
  // When props are provided (modal usage), skip parsing the URL entirely
  const urlQueryParams = useMemo(
    () => (leadIdProp ? {} : parseQuery(new URLSearchParams(window.location.search))),
    [leadIdProp],
  );
  const queryParams = useMemo(() => ({
    [CallBookingQueryParams.LeadId]: leadIdProp || urlQueryParams[CallBookingQueryParams.LeadId],
    [CallBookingQueryParams.Name]: nameProp || urlQueryParams[CallBookingQueryParams.Name],
    [CallBookingQueryParams.TimeZone]: timeZoneProp || urlQueryParams[CallBookingQueryParams.TimeZone],
  }), [leadIdProp, nameProp, timeZoneProp, urlQueryParams]);
  const [slotsAvailability, setSlotsAvailability] = useState(null);
  const [leadData, setLeadData] = useState(null);
  // Initial values derived from props/URL — when used as a modal, the parent remounts this
  // component on lead change by conditionally rendering the DialogModal, resetting all state.
  const [timeZone, setTimeZone] = useState(queryParams[CallBookingQueryParams.TimeZone]);
  const [selectedDate, setSelectedDate] = useState(moment().tz(queryParams[CallBookingQueryParams.TimeZone]));
  const [isBookingCancelled, setIsBookingCancelled] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [visibleDateRange, setVisibleDateRange] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBookingSuccess, setIsBookingSuccess] = useState(false);

  const { firebase: { remote } } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { userDoc } = useContext(LoggedInUserContext);

  const isQueryParamsValid = useMemo(() => REQUIRED_PARAMS.every((param) => queryParams[param]), [queryParams]);

  useEffect(() => {
    const fetchSlotSeatsAvailability = async () => {
      // fetch slot seats availability and lead data if the visible date range changes or the booking is cancelled
      if (visibleDateRange || isBookingCancelled) {
        setIsLoading(true);
        const { startStr, endStr } = visibleDateRange;
        const newSlotsAvailability = await getSlotSeatsAvailability(remote, startStr, endStr, timeZone);
        const leadDocumentData = await getLeadDetails(remote, queryParams[CallBookingQueryParams.LeadId]);
        if (isComponentMountedRef.current) {
          setSlotsAvailability(newSlotsAvailability);
          setIsBookingCancelled(false);
          setLeadData(leadDocumentData);
          setIsLoading(false);
        }
      }
    };
    fetchSlotSeatsAvailability();
  }, [
    visibleDateRange,
    timeZone,
    remote,
    isComponentMountedRef,
    queryParams,
    isBookingCancelled,
  ]);

  const slotsOfSelectedDate = useCallback((date) => {
    if (!slotsAvailability || !date) return [];
    const dateStr = date.format(DateFormat.DEFAULT_DATE_FORMAT);
    return slotsAvailability[dateStr] || [];
  }, [slotsAvailability]);

  const handleDateSelect = useCallback((info) => {
    const slotsForDate = slotsOfSelectedDate(moment(info.dateStr));
    const hasAvailableSlots = slotsForDate.some((slot) => slot.seatsRemaining > 0);
    if (hasAvailableSlots) {
      setSelectedDate(moment(info.dateStr));
      setSelectedSlot(null);
    }
  }, [slotsOfSelectedDate]);

  const dayCellContent = useCallback(
    (dayData) => <DayCell><DayNumber>{dayData.dayNumberText}</DayNumber></DayCell>,
    [],
  );

  const slotsForSelectedDate = useMemo(() => {
    if (!slotsAvailability || !selectedDate) return [];

    const selectedDateStr = selectedDate.format(DateFormat.DEFAULT_DATE_FORMAT);
    const slotsForDate = slotsOfSelectedDate(selectedDate);

    // Convert slot times to the selected timezone and filter by date
    return slotsForDate.map((slot) => {
      const slotTimeInTz = moment(slot.start).tz(timeZone);
      return {
        ...slot,
        displayTime: slotTimeInTz.format(TimeDurationFormat.TWELVE_HOUR_FORMAT),
        originalTime: slot.start,
        slotDateInTz: slotTimeInTz.format(DateFormat.DEFAULT_DATE_FORMAT),
        slotTimeInTz,
      };
    }).filter((slot) => slot.slotDateInTz === selectedDateStr);
  }, [
    slotsAvailability,
    selectedDate,
    timeZone,
    slotsOfSelectedDate,
  ]);

  const handleConfirmBooking = useCallback(async () => {
    setIsLoading(true);
    try {
      const { success, error } = await bookLeadCall(remote, selectedSlot.start, queryParams.leadId);
      if (success && isComponentMountedRef.current) {
        setIsBookingSuccess(true);
      } else {
        showToast(format(texts.errorBooking, { error: error.message }), { error: true });
      }
    } catch (error) {
      showToast(format(texts.errorBooking, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setShowConfirmDialog(false);
      }
    }
  }, [
    remote,
    queryParams,
    selectedSlot,
    setShowConfirmDialog,
    setIsBookingSuccess,
    isComponentMountedRef,
    showToast,
  ]);

  const selectAllow = useCallback((selectInfo) => {
    const slotsForDate = slotsOfSelectedDate(moment(selectInfo.startStr));
    return slotsForDate.some((slot) => slot.seatsRemaining > 0);
  }, [slotsOfSelectedDate]);

  const dayCellClassNames = useCallback((arg) => {
    const dateStr = moment(arg.date).format(DateFormat.DEFAULT_DATE_FORMAT);
    const slotsForDate = slotsOfSelectedDate(moment(dateStr));
    const hasAvailableSlots = slotsForDate.some((slot) => slot.seatsRemaining > 0);
    return hasAvailableSlots ? [] : ['fc-day-disabled'];
  }, [slotsOfSelectedDate]);

  const renderSlotsList = useCallback(() => {
    if (isLoading) {
      return <LoadingOverlay isLoading={isLoading} />;
    }
    if (selectedDate) {
      return (
        <SlotsList
          selectedDate={selectedDate}
          selectedDateSlots={slotsForSelectedDate}
          onSlotSelect={setSelectedSlot}
          selectedSlot={selectedSlot}
        />
      );
    }
    return null;
  }, [
    selectedDate,
    slotsForSelectedDate,
    isLoading,
    setSelectedSlot,
    selectedSlot,
  ]);

  const validRange = useMemo(() => ({ start: moment().startOf('month').format(DateFormat.DEFAULT_DATE_FORMAT) }), []);

  // Show error message if required parameters are missing
  if (!isQueryParamsValid) {
    return (
      <Container>
        <Alert severity="error">
          {texts.missingRequiredParams}
        </Alert>
      </Container>
    );
  }

  // Show booking success if the booking is successful or the lead's calendly meeting time is in the future
  if (isBookingSuccess || (leadData?.calendlyMeetingTime && moment().isBefore(moment(leadData.calendlyMeetingTime)))) {
    return (
      <Container>
        {!leadIdProp && <StyledToastContainer />}
        <BookingSuccess
          leadName={queryParams[CallBookingQueryParams.Name]}
          selectedSlot={selectedSlot}
          timeZone={timeZone}
          leadData={leadData}
          setBookingSuccess={setIsBookingSuccess}
          setBookingCancelled={setIsBookingCancelled}
          userName={userDoc.name}
        />
      </Container>
    );
  }

  return (
    <Container>
      {!leadIdProp && <StyledToastContainer />}
      <Header>
        <Title>{format(texts.bookCallFor, { name: queryParams[CallBookingQueryParams.Name] })}</Title>
        <TimeZoneLabel>{texts.leadTimeZone}</TimeZoneLabel>
        <TimeZoneSelectorWrapper>
          <AutoComplete
            options={timeZoneOptions}
            initialValue={timeZone}
            onChange={(selectedOption) => setTimeZone(selectedOption.id)}
            disableClearable
            filterOptions={(options, { inputValue }) => options.filter((option) => option.label.toLowerCase()
              .includes(inputValue.toLowerCase()))}
          />
        </TimeZoneSelectorWrapper>
        {(leadData?.calendlyMeetingTime && moment().isAfter(moment(leadData.calendlyMeetingTime))) && (
          <Alert severity="info">
            {format(texts.pastBookingInfo, {
              date: moment(leadData.calendlyMeetingTime).tz(timeZone).format(DateFormat.MONTH_NAME_DATE_FORMAT),
              time: moment(leadData.calendlyMeetingTime).tz(timeZone).format(TimeDurationFormat.TWELVE_HOUR_FORMAT),
              timeZone,
            })}
          </Alert>
        )}
      </Header>

      <MainContent>
        <CalendarSection>
          <>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next',
                center: 'title',
                right: '',
              }}
              validRange={validRange}
              selectable
              dayCellContent={dayCellContent}
              dayCellClassNames={dayCellClassNames}
              selectAllow={selectAllow}
              dateClick={handleDateSelect}
              datesSet={(args) => setVisibleDateRange(args)}
              fixedWeekCount={false}
              height="auto"
            />
          </>
        </CalendarSection>
        {renderSlotsList()}
      </MainContent>
      <Button
        full={false}
        onClick={() => setShowConfirmDialog(true)}
        disabled={!selectedSlot}
      >
        {texts.confirm}
      </Button>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmBooking}
        onCancel={() => setShowConfirmDialog(false)}
        dialogTexts={{
          title: texts.confirmBookingDialog.title,
          content: format(texts.confirmBookingDialog.content, {
            date: selectedDate?.format(DateFormat.MONTH_NAME_DATE_FORMAT),
            time: selectedSlot?.displayTime,
            timeZone,
          }),
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

BookLeadCall.propTypes = {
  leadId: PropTypes.string,
  name: PropTypes.string,
  timeZone: PropTypes.string,
};

BookLeadCall.defaultProps = {
  leadId: '',
  name: '',
  timeZone: '',
};

export default BookLeadCall;
